import React, { useState } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import MosaicEditModal from './MosaicEditModal';
import { translate } from '../../../services/translate';
import DeleteModal from '../../../components/DeleteModal';
import Toast from 'react-native-toast-message';
import { Pagination } from '../../../components/Pagination';
import { cameraService } from '../../../services/central-api/cameras';
import MyDataTable from '../../../components/MyDataTable';
import { ClientError } from '../../../services/central-api/base-service';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { CamerasParamList } from '../../../typings/Params';
import CustomButton from '../../../components/CustomButton';
import FilterInput from '../../../components/Filter/components/FilterInput';
import useFilters from '../../../components/Filter/hooks/useFilters';
import Filter from './Filters';
import DropdownButton from '../../../components/DropdownButton';
import MosaicShareModal from './MosaicShareModal';
import { useAuth } from '../../../context/Auth';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';
import { RootNavigation } from '../../../Routes';

export default function Mosaics({ navigation }: { navigation: RootNavigation; }) {
    const { user: authenticatedUser } = useAuth();
    const route = useRoute<RouteProp<CamerasParamList, 'Mosaic'>>();

    const [filters, setFilters] = useFilters<CamerasParamList['Mosaic']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });

    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
    const [mosaic, setMosaic] = useState<Mosaic>();
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [isShareModalVisible, setShareModalVisible] = useState<boolean>(false);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['camerasMosaics', filters],
        queryFn: () => cameraService.getMosaics(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    function handleFormClose() {
        setMosaic(undefined);
        setModalVisible(false);
        refetch();
    }

    function handleFormNew() {
        setMosaic(undefined);
        setModalVisible(true);
    }

    function handleShareFormClose() {
        setMosaic(undefined);
        setShareModalVisible(false);
    }

    function handleEditClick(mosaic: Mosaic) {
        setMosaic(mosaic);
        setModalVisible(true);
    }

    function handleShareClick(mosaic: Mosaic) {
        setMosaic(mosaic);
        setShareModalVisible(true);
    }

    function handleOpenClick(mosaic: Mosaic) {
        navigation.navigate('Cameras', { screen: 'MosaicDetail', params: { id: mosaic.id } });
    }

    function showDeleteConfirmationDialog(mosaic: Mosaic) {
        setMosaic(mosaic);
        setDeleteModalVisible(true);
    }

    async function confirmDelete(mosaic: Mosaic) {
        try {
            if (mosaic?.id) {
                await cameraService.deleteMosaic(mosaic.id);
                Toast.show({
                    type: 'sentinelxSuccess',
                    text1: translate('ActionSuccessfully'),
                });

                setDeleteModalVisible(false);
                refetch();
            }
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    return (
        <Container>
            <Header
                title={translate('mosaics')}
                actions={
                    <CustomButton text={translate('register')} onPress={handleFormNew} />
                }
            />
            <Card>
                <DataTableHeader
                    title={translate('registeredMosaics')}
                    zIndex={2}
                    filter={<Filter setTextFilter={setTextFilter} textFilter={textFilter} filters={filters} setFilters={setFilters} />}
                    filterInput={<FilterInput
                        value={textFilter}
                        onChange={setTextFilter}
                        placeholder={translate('typeToSearch')}
                        onKeyPress={handleKeyDown}
                    />}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[
                            {
                                name: translate('title'),
                                selector: row => row.title,
                                wrap: true,
                            },
                            {
                                button: true,
                                cell: row =>
                                    <DropdownButton
                                        actions={[
                                            {
                                                content: translate('watch'),
                                                onPress: () => {
                                                    handleOpenClick(row);
                                                }
                                            },
                                            ...(
                                                authenticatedUser?.permissions?.share_mosaic_with_all_acting_bodies ||
                                                    authenticatedUser?.permissions?.share_mosaic_with_same_acting_body ||
                                                    authenticatedUser?.permissions?.share_mosaic_with_same_acting_body_unit ?
                                                    [{
                                                        content: translate('share'),
                                                        onPress: () => {
                                                            handleShareClick(row);
                                                        }
                                                    }] : []
                                            ),
                                            {
                                                content: translate('edit'),
                                                onPress: () => {
                                                    handleEditClick(row);
                                                }
                                            },
                                            {
                                                content: translate('delete'),
                                                variant: 'danger',
                                                onPress: () => {
                                                    showDeleteConfirmationDialog(row);
                                                },
                                            }
                                        ]}
                                    />
                            }
                        ]}
                        data={data?.rows ?? []}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        totalItems={data?.count ?? 0}
                        currentPage={filters.page}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                    />}
                />
            </Card>
            <MosaicEditModal
                onClose={handleFormClose}
                mosaic={mosaic}
                isModalVisible={isModalVisible}
            />

            <MosaicShareModal
                onClose={handleShareFormClose}
                mosaic={mosaic}
                isModalVisible={isShareModalVisible}
            />

            <DeleteModal
                setModalVisible={setDeleteModalVisible}
                isModalVisible={isDeleteModalVisible}
                itemName={String(mosaic?.title) || ''}
                onSubmit={async () => {
                    if (mosaic) {
                        await confirmDelete(mosaic);
                    }
                }}
            />
        </Container>
    );
}
